import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Palette, Calendar, ChevronDown, ZoomIn, ZoomOut, RotateCcw, Link, Unlink, Crop, Edit3, ImageOff } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { getAvailableQuartersFromPage, formatVersionForPill, formatQuarterDisplay } from '../utils/companyDataUtils';
import { getLayoutConfig, getViewportDimensions } from '../config/dimensions';
import { AnnotationLayer } from './AnnotationLayer';

import { EditorConfig } from '../types';

interface CanvasAreaProps {
  asset: any;
  pageData: any;
  pageDataLoaded: boolean;
  editorState: any;
  config?: EditorConfig;
  annotationState: any;
  maskState: any;
  cropState: any;
  imageRef: React.RefObject<HTMLImageElement>;
  secondaryImageRef: React.RefObject<HTMLImageElement>;
  isCanvaLoading: { type: 'primary' | 'secondary' | null; message: string };
  companyLoading: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  isSecondaryDraggingRef: React.RefObject<boolean>;
  imageUrl: string;
  secondaryImageUrl: string;
  onVersionSelection: (version: string, description: string, target: 'primary' | 'secondary') => void;
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseUp: () => void;
  onWheel: (e: React.WheelEvent) => void;
  onSecondaryMouseDown: (e: React.MouseEvent) => void;
  onSecondaryMouseMove: (e: React.MouseEvent) => void;
  onSecondaryMouseUp: () => void;
  onSecondaryWheel: (e: React.WheelEvent) => void;
  onCanvaEdit: (type: 'primary' | 'secondary') => void;
  onZoom: (direction: 'in' | 'out', target?: 'primary' | 'secondary') => void;
  onReset: (target?: 'primary' | 'secondary') => void;
  onZoomPanLinkToggle: () => void;
  onPrimaryReset: () => void;
  onSecondaryReset: () => void;
  getPrimaryDisplayImageUrl: (asset: any) => string;
  getSecondaryDisplayImageUrl: (asset: any) => string;
  getOffsetCoordinates: (isPrimary?: boolean) => { x: number; y: number };
  getPrimaryQuarterData: () => any;
  getSecondaryQuarterData: () => any;
}

export const CanvasArea: React.FC<CanvasAreaProps> = ({
  asset,
  pageData,
  pageDataLoaded,
  editorState,
  config,
  annotationState,
  maskState,
  cropState,
  imageRef,
  secondaryImageRef,
  isCanvaLoading,
  companyLoading,
  containerRef,
  isSecondaryDraggingRef,
  imageUrl,
  secondaryImageUrl,
  onVersionSelection,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onWheel,
  onSecondaryMouseDown,
  onSecondaryMouseMove,
  onSecondaryMouseUp,
  onSecondaryWheel,
  onCanvaEdit,
  onZoom,
  onReset,
  onZoomPanLinkToggle,
  onPrimaryReset,
  onSecondaryReset,
  getPrimaryDisplayImageUrl,
  getSecondaryDisplayImageUrl,
  getOffsetCoordinates,
  getPrimaryQuarterData,
  getSecondaryQuarterData
}) => {
  const layoutConfig = getLayoutConfig();
  
  // Get viewport dimensions from config based on aspect ratio and comparison mode
  const baseViewportDimensions = getViewportDimensions(
    editorState.selectedAspectRatio || '1:1', 
    editorState.comparisonMode
  );
  
  // For now, keep the original scaling logic but make it more responsive
  const scaleFactor = config?.hideHeader ? 1.15 : 1.0;
  const viewportDimensions = {
    width: Math.round(baseViewportDimensions.width * scaleFactor),
    height: Math.round(baseViewportDimensions.height * scaleFactor)
  };
  
  return (
    <div className="flex-1 flex bg-gray-50 justify-center items-center px-4" style={{
      backgroundImage: 'radial-gradient(circle, #9ca3af 1px, transparent 1px)',
      backgroundSize: '16px 16px',
      paddingTop: `${layoutConfig.containerPadding}px`,
      paddingBottom: `${layoutConfig.containerPadding}px`
    }}>
      {/* White Container Card - Responsive to Aspect Ratio */}
      <div 
        className="bg-white rounded-lg shadow-lg flex flex-col relative group"
        style={{
          width: config?.forceCompactSize ? '75%' : (config?.hideHeader ? '95%' : '85%'),
          height: config?.forceCompactSize ? '85%' : (editorState.selectedAspectRatio === '1:1' ? (config?.hideHeader ? '95%' : '90%') : 'auto'),
          maxWidth: config?.forceCompactSize ? '75vw' : (editorState.selectedAspectRatio === '1:1' ? (config?.hideHeader ? 'min(95vh, 95vw)' : 'min(90vh, 85vw)') : (config?.hideHeader ? '95%' : '85%')),
          aspectRatio: editorState.selectedAspectRatio === '1:1' ? '1' : (editorState.selectedAspectRatio === '16:9' ? '16/9' : (editorState.selectedAspectRatio === '4:3' ? '4/3' : 'auto')),
          padding: `${layoutConfig.padding}px`
        }}
      >
        {/* Header/Subheader Controls - 10% of Content Canvas (hidden when config.hideHeader is true) */}
        {!config?.hideHeader && (
          <div className="flex justify-center items-center" style={{ height: '10%', paddingTop: '20px' }}>
            {editorState.selectedAspectRatio === '16:9' ? (
              <div className="flex gap-3 max-w-2xl w-full">
                <Input
                  id="header"
                  value={editorState.header}
                  onChange={(e) => editorState.setHeader(e.target.value)}
                  onFocus={() => editorState.setIsHeaderInputFocused(true)}
                  onBlur={() => editorState.setIsHeaderInputFocused(false)}
                  placeholder="Enter header text..."
                  className="text-sm text-center border-gray-300 flex-1"
                />
                <Input
                  id="subheader"
                  value={editorState.subheader}
                  onChange={(e) => editorState.setSubheader(e.target.value)}
                  onFocus={() => editorState.setIsSubheaderInputFocused(true)}
                  onBlur={() => editorState.setIsSubheaderInputFocused(false)}
                  placeholder="Enter subheader text..."
                  className="text-sm text-center border-gray-300 flex-1"
                />
              </div>
            ) : editorState.selectedAspectRatio === '4:3' ? (
              <div className="space-y-3 max-w-md w-full">
                <div>
                  <Input
                    id="header"
                    value={editorState.header}
                    onChange={(e) => editorState.setHeader(e.target.value)}
                    onFocus={() => editorState.setIsHeaderInputFocused(true)}
                    onBlur={() => editorState.setIsHeaderInputFocused(false)}
                    placeholder="Enter header text..."
                    className="text-sm text-center border-gray-300"
                  />
                </div>
                <div>
                  <Input
                    id="subheader"
                    value={editorState.subheader}
                    onChange={(e) => editorState.setSubheader(e.target.value)}
                    onFocus={() => editorState.setIsSubheaderInputFocused(true)}
                    onBlur={() => editorState.setIsSubheaderInputFocused(false)}
                    placeholder="Enter subheader text..."
                    className="text-sm text-center border-gray-300"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3 max-w-md w-full">
                <div>
                  <Input
                    id="header"
                    value={editorState.header}
                    onChange={(e) => editorState.setHeader(e.target.value)}
                    onFocus={() => editorState.setIsHeaderInputFocused(true)}
                    onBlur={() => editorState.setIsHeaderInputFocused(false)}
                    placeholder="Enter header text..."
                    className="text-sm text-center border-gray-300"
                  />
                </div>
                <div>
                  <Input
                    id="subheader"
                    value={editorState.subheader}
                    onChange={(e) => editorState.setSubheader(e.target.value)}
                    onFocus={() => editorState.setIsSubheaderInputFocused(true)}
                    onBlur={() => editorState.setIsSubheaderInputFocused(false)}
                    placeholder="Enter subheader text..."
                    className="text-sm text-center border-gray-300"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Canvas Container - Adjusts height based on whether header is hidden */}
        <div ref={containerRef} className="flex justify-center items-center relative" style={{ height: config?.hideHeader ? '90%' : '80%' }}>
          {/* Primary Toolbar - Single Mode or Dual Mode Primary */}
          <div 
            className={`absolute top-2 transition-opacity duration-200 z-[60] ${
              editorState.isHeaderInputFocused || editorState.isSubheaderInputFocused 
                ? 'opacity-0 pointer-events-none' 
                : 'opacity-0 group-hover:opacity-100'
            } ${
              editorState.comparisonMode === 'single' 
                ? 'left-1/2 transform -translate-x-1/2' 
                : editorState.comparisonMode === 'side_by_side' 
                ? 'left-1/4 transform -translate-x-1/2'
                : 'left-1/2 transform -translate-x-1/2'
            }`}
          >
            <div className="bg-white shadow-lg rounded-lg border border-gray-300 px-3 py-2 flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 hover:bg-gray-100"
                title="Zoom out - Primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onZoom('out', 'primary');
                }}
                disabled={editorState.zoomCoordinates.scale <= 0.5}
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 px-2 hover:bg-gray-100 text-xs min-w-[50px]"
                    title="Zoom level - Primary"
                  >
                    {Math.round(editorState.zoomCoordinates.scale * 100)}%
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center">
                  <DropdownMenuItem onClick={() => editorState.setZoomCoordinates({...editorState.zoomCoordinates, scale: 0.5})}>
                    50%
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => editorState.setZoomCoordinates({...editorState.zoomCoordinates, scale: 0.75})}>
                    75%
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => editorState.setZoomCoordinates({...editorState.zoomCoordinates, scale: 1})}>
                    100%
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => editorState.setZoomCoordinates({...editorState.zoomCoordinates, scale: 1.25})}>
                    125%
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => editorState.setZoomCoordinates({...editorState.zoomCoordinates, scale: 1.5})}>
                    150%
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => editorState.setZoomCoordinates({...editorState.zoomCoordinates, scale: 2})}>
                    200%
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => editorState.setZoomCoordinates({...editorState.zoomCoordinates, scale: 3})}>
                    300%
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => editorState.setZoomCoordinates({...editorState.zoomCoordinates, scale: 4})}>
                    400%
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 hover:bg-gray-100"
                title="Zoom in - Primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onZoom('in', 'primary');
                }}
                disabled={editorState.zoomCoordinates.scale >= 4.0}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              {/* Temporarily hidden - old reset zoom/pan button */}
              {false && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0 hover:bg-gray-100"
                  title="Reset zoom and pan - Primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onReset('primary');
                  }}
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              )}
              <div className="w-px h-6 bg-gray-300" />
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onCanvaEdit('primary');
                }}
                disabled={!getPrimaryDisplayImageUrl(asset) || isCanvaLoading.type === 'primary'}
                className="text-xs px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white shadow-md disabled:opacity-50 h-8"
                title="Edit primary image in Canva with current crop"
              >
                {isCanvaLoading.type === 'primary' ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Palette className="h-3 w-3 mr-1" />
                )}
                {isCanvaLoading.type === 'primary' ? isCanvaLoading.message : 'Edit'}
              </Button>
              {/* Reset button - only show in markup mode */}
              {editorState.primaryImageMode === 'markup' && asset.primary_markup_url && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPrimaryReset();
                  }}
                  className="text-xs px-2 py-1 bg-white hover:bg-gray-50 shadow-md h-8 text-red-600 hover:text-red-700"
                  title="Reset to Crop Mode (removes Canva markup)"
                >
                  <RotateCcw className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          {/* Secondary Toolbar - Only in Dual Modes */}
          {editorState.comparisonMode !== 'single' && (
            <div 
              className={`absolute transition-opacity duration-200 z-[60] ${
                editorState.isHeaderInputFocused || editorState.isSubheaderInputFocused 
                  ? 'opacity-0 pointer-events-none' 
                  : 'opacity-0 group-hover:opacity-100'
              } ${
                editorState.comparisonMode === 'side_by_side' 
                  ? 'top-2 right-1/4 transform translate-x-1/2'
                  : 'bottom-2 left-1/2 transform -translate-x-1/2'
              }`}
            >
              <div className="bg-white shadow-lg rounded-lg border border-gray-300 px-3 py-2 flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0 hover:bg-gray-100"
                  title="Zoom out - Secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onZoom('out', 'secondary');
                  }}
                  disabled={editorState.secondaryZoomCoordinates.scale <= 0.5}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 px-2 hover:bg-gray-100 text-xs min-w-[50px]"
                      title="Zoom level - Secondary"
                    >
                      {Math.round(editorState.secondaryZoomCoordinates.scale * 100)}%
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center">
                    <DropdownMenuItem onClick={() => editorState.setSecondaryZoomCoordinates({...editorState.secondaryZoomCoordinates, scale: 0.5})}>
                      50%
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => editorState.setSecondaryZoomCoordinates({...editorState.secondaryZoomCoordinates, scale: 0.75})}>
                      75%
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => editorState.setSecondaryZoomCoordinates({...editorState.secondaryZoomCoordinates, scale: 1})}>
                      100%
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => editorState.setSecondaryZoomCoordinates({...editorState.secondaryZoomCoordinates, scale: 1.25})}>
                      125%
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => editorState.setSecondaryZoomCoordinates({...editorState.secondaryZoomCoordinates, scale: 1.5})}>
                      150%
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => editorState.setSecondaryZoomCoordinates({...editorState.secondaryZoomCoordinates, scale: 2})}>
                      200%
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => editorState.setSecondaryZoomCoordinates({...editorState.secondaryZoomCoordinates, scale: 3})}>
                      300%
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => editorState.setSecondaryZoomCoordinates({...editorState.secondaryZoomCoordinates, scale: 4})}>
                      400%
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0 hover:bg-gray-100"
                  title="Zoom in - Secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onZoom('in', 'secondary');
                  }}
                  disabled={editorState.secondaryZoomCoordinates.scale >= 4.0}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
                {/* Temporarily hidden - old reset zoom/pan button */}
                {false && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0 hover:bg-gray-100"
                    title="Reset zoom and pan - Secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      onReset('secondary');
                    }}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}
                <div className="w-px h-6 bg-gray-300" />
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCanvaEdit('secondary');
                  }}
                  disabled={!getSecondaryDisplayImageUrl(asset) || isCanvaLoading.type === 'secondary'}
                  className="text-xs px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white shadow-md disabled:opacity-50 h-8"
                  title="Edit secondary image in Canva with current crop"
                >
                  {isCanvaLoading.type === 'secondary' ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <Palette className="h-3 w-3 mr-1" />
                  )}
                  {isCanvaLoading.type === 'secondary' ? isCanvaLoading.message : 'Edit'}
                </Button>
                {/* Reset button - only show in markup mode */}
                {editorState.secondaryImageMode === 'markup' && asset.secondary_markup_url && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSecondaryReset();
                    }}
                    className="text-xs px-2 py-1 bg-white hover:bg-gray-50 shadow-md h-8 text-red-600 hover:text-red-700"
                    title="Reset to Crop Mode (removes Canva markup)"
                  >
                    <RotateCcw className="h-3 w-3" />
                  </Button>
                )}
                {/* Link/Unlink control */}
                <Button
                  size="sm"
                  variant={editorState.isZoomPanLinked ? 'default' : 'ghost'}
                  className="h-8 w-8 p-0 hover:bg-gray-100 ml-2"
                  title={editorState.isZoomPanLinked ? 'Linked - Click to unlink' : 'Unlinked - Click to link'}
                  onClick={(e) => {
                    e.stopPropagation();
                    onZoomPanLinkToggle();
                  }}
                >
                  {editorState.isZoomPanLinked ? (
                    <Link className="h-4 w-4" />
                  ) : (
                    <Unlink className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          )}
          {/* Unified Viewport - consistent dimensions across all modes */}
          <div
            className="relative overflow-hidden group"
            style={{
              width: editorState.comparisonMode === 'side_by_side' 
                ? `${viewportDimensions.width * 2 + layoutConfig.gap}px` // Two panels + gap
                : editorState.zoomCoordinates.viewportWidth,
              height: editorState.comparisonMode === 'side_by_side'
                ? `${viewportDimensions.height}px`
                : editorState.zoomCoordinates.viewportHeight,
            }}
          >
            {editorState.comparisonMode === 'single' ? (
              /* Single Panel Mode */
              <div 
                className="absolute inset-0 border-2 border-transparent group-hover:border-dashed group-hover:border-blue-400 transition-all duration-200 z-20"
                style={{
                  cursor: annotationState.isAnnotationMode ? 'crosshair' : 
                         (maskState.isMaskMode ? 'crosshair' : 
                         (editorState.isDraggingRef?.current ? 'grabbing' : 'grab'))
                }}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
                onWheel={onWheel}
              >
                {/* Primary Image */}
                <img
                  ref={imageRef}
                  src={imageUrl}
                  alt="Asset preview"
                  className="absolute shadow-lg z-30"
                  style={{
                    transform: `translate(${editorState.zoomCoordinates.x}px, ${editorState.zoomCoordinates.y}px) scale(${editorState.zoomCoordinates.scale})`,
                    transformOrigin: '0 0',
                    transition: editorState.isDraggingRef?.current ? 'none' : 'transform 0.2s ease'
                  }}
                  draggable={false}
                />

                {/* Primary Annotation Layer */}
                <AnnotationLayer
                  annotations={annotationState.annotations}
                  selectedAnnotationId={annotationState.selectedAnnotationId}
                  setSelectedAnnotationId={annotationState.setSelectedAnnotationId}
                  annotationStyle={annotationState.annotationStyle}
                  isAnnotationMode={annotationState.isAnnotationMode}
                  currentAnnotation={annotationState.currentAnnotation}
                  isDrawing={annotationState.isDrawingAnnotation}
                  onDeleteAnnotation={annotationState.deleteAnnotation}
                />
              </div>
            ) : (
              /* Dual Panel Mode */
              <div className="absolute inset-0 flex" style={{
                flexDirection: editorState.comparisonMode === 'side_by_side' ? 'row' : 'column',
                gap: `${layoutConfig.gap}px` // Gap between images
              }}>
                {/* Primary Panel */}
                <div 
                  className="relative overflow-hidden border-2 border-transparent hover:border-dashed hover:border-blue-400 transition-all duration-200 z-20"
                  style={{
                    width: editorState.comparisonMode === 'side_by_side' ? `${viewportDimensions.width}px` : 
                           (editorState.comparisonMode === 'stacked' ? `${viewportDimensions.width}px` : `${editorState.zoomCoordinates.viewportWidth}px`),
                    height: editorState.comparisonMode === 'side_by_side' ? `${viewportDimensions.height}px` : 
                            (editorState.comparisonMode === 'stacked' ? `${viewportDimensions.height}px` : `calc(50% - 25px)`),
                    cursor: annotationState.isAnnotationMode ? 'crosshair' : 
                           (maskState.isMaskMode ? 'crosshair' : 
                           (editorState.isDraggingRef?.current ? 'grabbing' : 'grab'))
                  }}
                  onMouseDown={onMouseDown}
                  onMouseMove={onMouseMove}
                  onMouseUp={onMouseUp}
                  onMouseLeave={onMouseUp}
                  onWheel={onWheel}
                >
                  <img
                    ref={imageRef}
                    src={imageUrl}
                    alt="Primary preview"
                    className="absolute shadow-lg z-30"
                    style={{
                      transform: `translate(${editorState.zoomCoordinates.x}px, ${editorState.zoomCoordinates.y}px) scale(${editorState.zoomCoordinates.scale})`,
                      transformOrigin: '0 0',
                      transition: editorState.isDraggingRef?.current ? 'none' : 'transform 0.2s ease'
                    }}
                    draggable={false}
                  />

                  {/* Primary Annotation Layer */}
                  <AnnotationLayer
                    annotations={annotationState.annotations}
                    selectedAnnotationId={annotationState.selectedAnnotationId}
                    setSelectedAnnotationId={annotationState.setSelectedAnnotationId}
                    annotationStyle={annotationState.annotationStyle}
                    isAnnotationMode={annotationState.isAnnotationMode}
                    currentAnnotation={annotationState.currentAnnotation}
                    isDrawing={annotationState.isDrawingAnnotation}
                    onDeleteAnnotation={annotationState.deleteAnnotation}
                    clearOtherSelection={() => annotationState.setSelectedSecondaryAnnotationId(null)}
                  />
                </div>

                {/* Secondary Panel */}
                <div 
                  className="relative overflow-hidden border-2 border-transparent hover:border-dashed hover:border-blue-400 transition-all duration-200 z-20"
                  style={{
                    width: editorState.comparisonMode === 'side_by_side' ? `${viewportDimensions.width}px` : 
                           (editorState.comparisonMode === 'stacked' ? `${viewportDimensions.width}px` : `${editorState.secondaryZoomCoordinates.viewportWidth}px`),
                    height: editorState.comparisonMode === 'side_by_side' ? `${viewportDimensions.height}px` : 
                            (editorState.comparisonMode === 'stacked' ? `${viewportDimensions.height}px` : `calc(50% - 25px)`),
                    cursor: annotationState.isAnnotationMode ? 'crosshair' : 
                           (maskState.isMaskMode ? 'crosshair' : 
                           (isSecondaryDraggingRef.current ? 'grabbing' : 'grab'))
                  }}
                  onMouseDown={onSecondaryMouseDown}
                  onMouseMove={onSecondaryMouseMove}
                  onMouseUp={onSecondaryMouseUp}
                  onMouseLeave={onSecondaryMouseUp}
                  onWheel={onSecondaryWheel}
                >
                  {secondaryImageUrl ? (
                    <img
                      ref={secondaryImageRef}
                      src={secondaryImageUrl}
                      alt="Secondary preview"
                      className="absolute shadow-lg z-30"
                      style={{
                        transform: `translate(${editorState.secondaryZoomCoordinates.x}px, ${editorState.secondaryZoomCoordinates.y}px) scale(${editorState.secondaryZoomCoordinates.scale})`,
                        transformOrigin: '0 0',
                        transition: isSecondaryDraggingRef.current ? 'none' : 'transform 0.2s ease'
                      }}
                      draggable={false}
                    />
                  ) : (
                    // Empty state for no secondary image
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50 border-2 border-dashed border-gray-300">
                      <div className="text-center text-gray-500">
                        <ImageOff className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm font-medium mb-1">No secondary image</p>
                        <p className="text-xs opacity-75">Select a version to compare</p>
                      </div>
                    </div>
                  )}

                  {/* Secondary Annotation Layer */}
                  <AnnotationLayer
                    annotations={annotationState.secondaryAnnotations}
                    selectedAnnotationId={annotationState.selectedSecondaryAnnotationId}
                    setSelectedAnnotationId={annotationState.setSelectedSecondaryAnnotationId}
                    annotationStyle={annotationState.annotationStyle}
                    isAnnotationMode={annotationState.isAnnotationMode}
                    currentAnnotation={annotationState.currentSecondaryAnnotation}
                    isDrawing={annotationState.isDrawingSecondaryAnnotation}
                    onDeleteAnnotation={annotationState.deleteSecondaryAnnotation}
                    clearOtherSelection={() => annotationState.setSelectedAnnotationId(null)}
                  />
                </div>
              </div>
            )}
            
            {/* Version Selector Overlays - Hidden when config.hideHeader is true */}
            {!config?.hideHeader && (editorState.comparisonMode === 'single' ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs font-medium transition-colors cursor-pointer z-20 flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Calendar className="h-3 w-3" />
                    {getPrimaryQuarterData() ? 
                      formatVersionForPill(getPrimaryQuarterData()!.version) : 
                      formatVersionForPill(editorState.primaryImageData?.version || asset.version)
                    }
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64" align="center">
                  {!companyLoading && pageDataLoaded && getAvailableQuartersFromPage(pageData).map((quarterData: any) => (
                    <DropdownMenuItem 
                      key={quarterData.quarter}
                      onClick={() => {
                        onVersionSelection(quarterData.version, quarterData.quarter, 'primary');
                      }}
                    >
                      {formatQuarterDisplay(quarterData.quarter, quarterData.version)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                {/* Primary and Secondary version selector pills for dual mode */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="absolute bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs font-medium transition-colors cursor-pointer z-20 flex items-center gap-1 shadow-md"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        left: editorState.comparisonMode === 'side_by_side' ? (
                          editorState.selectedAspectRatio === '1:1' ? '24%' : '25%'
                        ) : '50%',
                        top: editorState.comparisonMode === 'side_by_side' ? 'auto' : `calc(50% - 30px)`,
                        bottom: editorState.comparisonMode === 'side_by_side' ? '8px' : 'auto',
                        transform: editorState.comparisonMode === 'side_by_side' ? 'translateX(-50%)' : 'translateX(-50%)'
                      }}
                    >
                      <Calendar className="h-3 w-3" />
                      {getPrimaryQuarterData() ? 
                        formatVersionForPill(getPrimaryQuarterData()!.version) : 
                        formatVersionForPill(editorState.primaryImageData?.version || asset.version)
                      }
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-64" align="center">
                    {!companyLoading && pageDataLoaded && getAvailableQuartersFromPage(pageData).map((quarterData: any) => (
                      <DropdownMenuItem 
                        key={quarterData.quarter}
                        onClick={() => {
                          onVersionSelection(quarterData.version, quarterData.quarter, 'primary');
                        }}
                      >
                        {formatQuarterDisplay(quarterData.quarter, quarterData.version)}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="absolute bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs font-medium transition-colors cursor-pointer z-20 flex items-center gap-1 shadow-md"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        left: editorState.comparisonMode === 'side_by_side' ? (
                          editorState.selectedAspectRatio === '1:1' ? '76%' : '75%'
                        ) : '50%',
                        top: editorState.comparisonMode === 'side_by_side' ? 'auto' : 'auto',
                        bottom: editorState.comparisonMode === 'side_by_side' ? '8px' : '8px',
                        transform: editorState.comparisonMode === 'side_by_side' ? 'translateX(-50%)' : 'translateX(-50%)'
                      }}
                    >
                      <Calendar className="h-3 w-3" />
                      {getSecondaryQuarterData() ? 
                        formatVersionForPill(getSecondaryQuarterData()!.version) : 
                        (editorState.secondaryImageData?.version ? formatVersionForPill(editorState.secondaryImageData.version) : 'Select Version')
                      }
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-64" align="center">
                    {!companyLoading && pageDataLoaded && getAvailableQuartersFromPage(pageData).map((quarterData: any) => (
                      <DropdownMenuItem 
                        key={quarterData.quarter}
                        onClick={() => {
                          onVersionSelection(quarterData.version, quarterData.quarter, 'secondary');
                        }}
                      >
                        {formatQuarterDisplay(quarterData.quarter, quarterData.version)}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ))}
        </div>
        </div>
        
        {/* Content Canvas Footer - 10% contained within white canvas */}
        <div 
          className="flex items-center justify-between"
          style={{ 
            backgroundColor: '#132442',
            height: '10%',
            position: 'absolute',
            bottom: '0',
            left: '0',
            right: '0',
            padding: '0 16px',
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px'
          }}
        >
          {/* Company Logo and Name - Left Side */}
          <div className="flex items-center gap-3">
            {(() => {
              const logoUrl = asset?.page?.company?.logo_url || asset?.company?.logo_url || asset?.logo_url;
              
              if (logoUrl) {
                return (
                  <img
                    src={logoUrl}
                    alt={`${asset.slug || asset?.company?.name || 'Company'} logo`}
                    className="h-8 object-contain opacity-80"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                );
              }
              
              return (
                <div className="w-8 h-8 bg-white/20 rounded flex items-center justify-center">
                  <span className="text-white text-sm font-bold">
                    {asset.slug ? asset.slug.charAt(0).toUpperCase() : '?'}
                  </span>
                </div>
              );
            })()}
            <div className="text-white opacity-80 text-lg font-medium">
              {asset.slug ? asset.slug.charAt(0).toUpperCase() + asset.slug.slice(1) : 'Unknown Company'}
            </div>
          </div>
          
          {/* PricingSaaS Branding - Right Side */}
          <div className="flex items-center gap-2">
            <span className="text-white opacity-60 text-sm">Curated by</span>
            <img
              src="https://res.cloudinary.com/dd6dkaan9/image/upload/v1756958890/WordmarkWhite_1_d3fqlz.png"
              alt="PricingSaaS logo"
              className="h-6 object-contain opacity-80"
            />
          </div>
        </div>
      </div>
    </div>
  );
};